import { IMeetingRoomSettingsDays } from '../../settings/types'
import {
  getWorkingDayIndexes,
  getWorkingDaysFromIndexes,
} from 'domain/feature/meeting-room/shared/workingDays'
import { RulesOfUseTabData } from '../RulesOfUseTab'
import {
  CustomQuestion,
  CustomSection,
  FixedField,
  QuestionOption,
  QuestionType,
  RequestFormState,
} from '../ApplicationFormsTab/types'
import {
  MEETING_ROOM_FIXED_FIELD,
  MEETING_ROOM_QUESTION_TYPE,
  MEETING_ROOM_SECTION_VISIBILITY,
  MeetingRoomFixedFieldRequest,
  MeetingRoomFixedFieldValue,
  MeetingRoomOptionRequest,
  MeetingRoomQuestionRequest,
  MeetingRoomQuestionTypeValue,
  MeetingRoomSectionRequest,
  MeetingRoomSectionVisibilityValue,
  MeetingRoomSettingsSaveRequest,
} from './types'

export const EMPTY_SETTINGS_ID = '00000000-0000-0000-0000-000000000000'

const REQUIRED_FIXED_FIELDS: MeetingRoomFixedFieldRequest[] = [
  { fieldName: MEETING_ROOM_FIXED_FIELD.FULL_NAME, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.ORGANIZATION_NAME, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.EMAIL, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.PHONE, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.START_DATE, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.END_DATE, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.START_TIME, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.END_TIME, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.PARTICIPANTS, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.EVENT_NAME, isVisible: true, isRequired: true },
  { fieldName: MEETING_ROOM_FIXED_FIELD.EVENT_DESCRIPTION, isVisible: true, isRequired: true },
]

const QUESTION_TYPE_MAP: Record<QuestionType, MeetingRoomQuestionTypeValue> = {
  short_text: MEETING_ROOM_QUESTION_TYPE.SHORT_TEXT,
  long_text: MEETING_ROOM_QUESTION_TYPE.LONG_TEXT,
  date: MEETING_ROOM_QUESTION_TYPE.DATE,
  number: MEETING_ROOM_QUESTION_TYPE.NUMBER,
  file_upload: MEETING_ROOM_QUESTION_TYPE.FILE_UPLOAD,
  link: MEETING_ROOM_QUESTION_TYPE.LINK,
  dropdown: MEETING_ROOM_QUESTION_TYPE.DROPDOWN,
  multiple_choice: MEETING_ROOM_QUESTION_TYPE.MULTIPLE_CHOICE,
  checkbox: MEETING_ROOM_QUESTION_TYPE.CHECKBOX,
}

const toTimeSpanString = (value: string): string => {
  if (!value) return '00:00:00'
  const parts = value.split(':')
  const hh = parts[0]?.padStart(2, '0') ?? '00'
  const mm = parts[1]?.padStart(2, '0') ?? '00'
  const ss = parts[2]?.padStart(2, '0') ?? '00'
  return `${hh}:${mm}:${ss}`
}

const resolveSectionVisibility = (
  section: CustomSection,
): MeetingRoomSectionVisibilityValue => {
  const isEvent = section.eventChecked === true
  const isMeeting = section.meetingChecked === true
  if (isEvent && isMeeting) return MEETING_ROOM_SECTION_VISIBILITY.ALL
  if (isEvent) return MEETING_ROOM_SECTION_VISIBILITY.EVENT
  if (isMeeting) return MEETING_ROOM_SECTION_VISIBILITY.COWORKING
  return MEETING_ROOM_SECTION_VISIBILITY.ALL
}

const mapOption = (option: QuestionOption, index: number): MeetingRoomOptionRequest => ({
  id: option.id,
  text: option.text,
  sortOrder: option.sortOrder ?? index,
})

const mapQuestion = (
  question: CustomQuestion,
  index: number,
): MeetingRoomQuestionRequest => ({
  id: question.id,
  text: question.text,
  description: question.description,
  type: QUESTION_TYPE_MAP[question.type],
  isRequired: question.required,
  sortOrder: question.sortOrder ?? index,
  options: (question.options ?? []).map(mapOption),
})

const mapSection = (section: CustomSection, index: number): MeetingRoomSectionRequest => ({
  id: section.id,
  name: section.name,
  visible: resolveSectionVisibility(section),
  sortOrder: section.sortOrder ?? index,
  questions: section.questions.map(mapQuestion),
})

interface SettingsFormData {
  publicSlug: string
  days: IMeetingRoomSettingsDays
  openTime: string
  closeTime: string
  meetingSpacesLimit: number
  meetingDaysLimit: number
  meetingMinLeadTime: number
  meetingMaxDuration: number
  meetingAutomaticApproval: boolean
  eventSpacesLimit: number
  eventDaysLimit: number
  eventMinLeadTime: number
  eventInterestDemonstration: boolean
}

interface RequestFormsData {
  checkboxText: string
  checkboxRequired: boolean
  warningText: string
  fixedFields: FixedField[]
  sections: CustomSection[]
}

export const buildSettingsSavePayload = (
  settingsForm: SettingsFormData,
  requestForms: RequestFormsData,
  settingsId: string = EMPTY_SETTINGS_ID,
): MeetingRoomSettingsSaveRequest => {
  const fixedFieldsSource = requestForms.fixedFields.length > 0
    ? requestForms.fixedFields
    : REQUIRED_FIXED_FIELDS

  return {
    data: {
      id: settingsId,
      publicSlug: settingsForm.publicSlug,
      workingDays: getWorkingDayIndexes(settingsForm.days),
      openingHour: toTimeSpanString(settingsForm.openTime),
      closingHour: toTimeSpanString(settingsForm.closeTime),
      acceptanceCheckboxText: requestForms.checkboxText,
      acceptanceCheckboxIsRequired: requestForms.checkboxRequired,
      warningMessage: requestForms.warningText,
      meetingSpacesLimit: settingsForm.meetingSpacesLimit,
      meetingDaysLimit: settingsForm.meetingDaysLimit,
      meetingMinLeadTime: settingsForm.meetingMinLeadTime,
      meetingMaxDuration: settingsForm.meetingMaxDuration,
      meetingAutomaticApproval: settingsForm.meetingAutomaticApproval,
      eventSpacesLimit: settingsForm.eventSpacesLimit,
      eventDaysLimit: settingsForm.eventDaysLimit,
      eventMinLeadTime: settingsForm.eventMinLeadTime,
      eventInterestDemonstration: settingsForm.eventInterestDemonstration,
      fixedFields: fixedFieldsSource.map((f) => ({
        id: f.id,
        fieldName: f.fieldName as MeetingRoomFixedFieldValue,
        isVisible: f.isVisible,
        isRequired: f.isRequired,
      })),
      sections: requestForms.sections.map(mapSection),
    },
  }
}

const REVERSE_QUESTION_TYPE_MAP: Record<MeetingRoomQuestionTypeValue, QuestionType> = {
  [MEETING_ROOM_QUESTION_TYPE.SHORT_TEXT]: 'short_text',
  [MEETING_ROOM_QUESTION_TYPE.LONG_TEXT]: 'long_text',
  [MEETING_ROOM_QUESTION_TYPE.DATE]: 'date',
  [MEETING_ROOM_QUESTION_TYPE.NUMBER]: 'number',
  [MEETING_ROOM_QUESTION_TYPE.FILE_UPLOAD]: 'file_upload',
  [MEETING_ROOM_QUESTION_TYPE.LINK]: 'link',
  [MEETING_ROOM_QUESTION_TYPE.DROPDOWN]: 'dropdown',
  [MEETING_ROOM_QUESTION_TYPE.MULTIPLE_CHOICE]: 'multiple_choice',
  [MEETING_ROOM_QUESTION_TYPE.CHECKBOX]: 'checkbox',
}

const fromTimeSpanString = (value?: string | null): string => {
  if (!value) return ''
  const parts = value.split(':')
  return `${parts[0] ?? '00'}:${parts[1] ?? '00'}`
}

interface MeetingRoomSettingsApiOption {
  id: string
  text: string
  sortOrder: number
}

interface MeetingRoomSettingsApiQuestion {
  id: string
  text: string
  description?: string
  type: MeetingRoomQuestionTypeValue
  isRequired: boolean
  sortOrder: number
  options: MeetingRoomSettingsApiOption[]
}

interface MeetingRoomSettingsApiSection {
  id: string
  name: string
  visible: MeetingRoomSectionVisibilityValue
  sortOrder: number
  questions: MeetingRoomSettingsApiQuestion[]
}

interface MeetingRoomSettingsApiFixedField {
  id: string
  fieldName: number
  isVisible: boolean
  isRequired: boolean
}

export interface MeetingRoomSettingsGetData {
  id: string
  publicSlug?: string | null
  workingDays: number[]
  openingHour?: string | null
  closingHour?: string | null
  acceptanceCheckboxText?: string | null
  acceptanceCheckboxIsRequired: boolean
  warningMessage?: string | null
  meetingSpacesLimit: number
  meetingDaysLimit: number
  meetingMinLeadTime: number
  meetingMaxDuration: number
  meetingAutomaticApproval: boolean
  eventSpacesLimit: number
  eventDaysLimit: number
  eventMinLeadTime: number
  eventInterestDemonstration: boolean
  fixedFields: MeetingRoomSettingsApiFixedField[]
  sections: MeetingRoomSettingsApiSection[]
}

export interface MeetingRoomSettingsGetResponse {
  id: string
  data: MeetingRoomSettingsGetData
}

const mapVisibilityToChecks = (
  visible: MeetingRoomSectionVisibilityValue,
): { eventChecked: boolean; meetingChecked: boolean } => {
  if (visible === MEETING_ROOM_SECTION_VISIBILITY.EVENT) {
    return { eventChecked: true, meetingChecked: false }
  }
  if (visible === MEETING_ROOM_SECTION_VISIBILITY.COWORKING) {
    return { eventChecked: false, meetingChecked: true }
  }
  return { eventChecked: true, meetingChecked: true }
}

const mapOptionFromApi = (option: MeetingRoomSettingsApiOption): QuestionOption => ({
  id: option.id,
  text: option.text,
  sortOrder: option.sortOrder,
})

const mapQuestionFromApi = (question: MeetingRoomSettingsApiQuestion): CustomQuestion => ({
  id: question.id,
  text: question.text,
  description: question.description,
  type: REVERSE_QUESTION_TYPE_MAP[question.type] ?? 'short_text',
  required: question.isRequired,
  hiddenFromEvaluators: false,
  expanded: true,
  sortOrder: question.sortOrder,
  options: [...question.options]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(mapOptionFromApi),
})

const mapSectionFromApi = (section: MeetingRoomSettingsApiSection): CustomSection => ({
  id: section.id,
  name: section.name,
  sortOrder: section.sortOrder,
  ...mapVisibilityToChecks(section.visible),
  questions: [...section.questions]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(mapQuestionFromApi),
  expanded: true,
})

const mapFixedFieldFromApi = (field: MeetingRoomSettingsApiFixedField): FixedField => ({
  id: field.id,
  fieldName: field.fieldName,
  isVisible: field.isVisible,
  isRequired: field.isRequired,
})

export interface MappedSettingsFormState {
  rulesData: RulesOfUseTabData
  formsData: RequestFormState
}

export const mapGetResponseToFormState = (
  response: MeetingRoomSettingsGetResponse,
): MappedSettingsFormState => {
  const data = response.data
  const rulesData: RulesOfUseTabData = {
    publicSlug: data.publicSlug ?? '',
    days: getWorkingDaysFromIndexes(data.workingDays ?? []),
    openTime: fromTimeSpanString(data.openingHour),
    closeTime: fromTimeSpanString(data.closingHour),
    meetingSpacesLimit: data.meetingSpacesLimit,
    meetingDaysLimit: data.meetingDaysLimit,
    meetingMinLeadTime: data.meetingMinLeadTime,
    meetingMaxDuration: data.meetingMaxDuration,
    meetingAutomaticApproval: data.meetingAutomaticApproval,
    eventSpacesLimit: data.eventSpacesLimit,
    eventDaysLimit: data.eventDaysLimit,
    eventMinLeadTime: data.eventMinLeadTime,
    eventInterestDemonstration: data.eventInterestDemonstration,
  }
  const formsData: RequestFormState = {
    checkboxText: data.acceptanceCheckboxText ?? '',
    checkboxRequired: data.acceptanceCheckboxIsRequired ?? false,
    warningText: data.warningMessage ?? '',
    fixedFields: (data.fixedFields ?? []).map(mapFixedFieldFromApi),
    sections: [...(data.sections ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(mapSectionFromApi),
    fixedSectionExpanded: false,
  }
  return { rulesData, formsData }
}
